import { PageHeading } from "./learning_lobby";
import { LearningStatistics, ResultSummary } from "./learning_model";

export function activityDays(
  activity: LearningStatistics["activity"],
  now = new Date(),
) {
  const today = new Date(now.getTime() + 3 * 3600000);
  const midnight = Date.UTC(
    today.getUTCFullYear(),
    today.getUTCMonth(),
    today.getUTCDate(),
  );
  const counts = new Map(activity.map((day) => [day.date, day.count]));
  return Array.from({ length: 365 }, (_, index) => {
    const date = new Date(midnight - (364 - index) * 86400000);
    const key = date.toISOString().slice(0, 10);
    return {
      date: key,
      count: counts.get(key) || 0,
      weekday: (date.getUTCDay() + 6) % 7,
    };
  });
}

function ActivityCalendar({ statistics }: { statistics: LearningStatistics }) {
  const days = activityDays(statistics.activity);
  const weeks = Math.ceil((days.length + days[0].weekday) / 7);
  const months = days.flatMap((day, index) => {
    if (index !== 0 && !day.date.endsWith("-01")) return [];
    const column = Math.floor((index + days[0].weekday) / 7) + 1;
    if (column > weeks - 2) return [];
    return [
      {
        key: day.date,
        column,
        label: new Date(`${day.date}T12:00:00+03:00`).toLocaleDateString(
          "ru-RU",
          { month: "short", timeZone: "Europe/Moscow" },
        ),
      },
    ];
  });
  const count = days.reduce((total, day) => total + day.count, 0);
  return (
    <section className="activity-calendar" aria-labelledby="activity-title">
      <div className="section-heading">
        <h3 id="activity-title">Активность</h3>
        <p className="portal-muted">За год завершено тестов: {count}</p>
      </div>
      <div className="activity-scroll">
        <div
          className="activity-months"
          aria-hidden="true"
          style={{ gridTemplateColumns: `repeat(${weeks}, 13px)` }}
        >
          {months.map((month) => (
            <span
              key={month.key}
              style={{ gridColumn: `${month.column} / span 3` }}
            >
              {month.label}
            </span>
          ))}
        </div>
        <div className="activity-chart">
          <div className="activity-weekdays" aria-hidden="true">
            <span>Пн</span>
            <span />
            <span>Ср</span>
            <span />
            <span>Пт</span>
            <span />
            <span />
          </div>
          <div
            className="activity-grid"
            role="list"
            aria-label="Завершённые тесты по дням, московское время"
          >
            {Array.from({ length: days[0].weekday }, (_, index) => (
              <div
                className="activity-spacer"
                key={`spacer-${index}`}
                aria-hidden="true"
              />
            ))}
            {days.map((day) => {
              const level =
                day.count === 0
                  ? 0
                  : day.count === 1
                    ? 1
                    : day.count <= 3
                      ? 2
                      : day.count <= 6
                        ? 3
                        : 4;
              const label = `${new Date(`${day.date}T12:00:00+03:00`).toLocaleDateString("ru-RU", { timeZone: "Europe/Moscow" })}: завершено тестов — ${day.count}`;
              return (
                <div
                  key={day.date}
                  role="listitem"
                  aria-label={label}
                  title={label}
                  className={`activity-day activity-level-${level}`}
                />
              );
            })}
          </div>
        </div>
      </div>
      <div
        className="activity-legend"
        aria-label="Чем светлее ячейка, тем больше завершённых тестов"
      >
        <span>Меньше</span>
        {[0, 1, 2, 3, 4].map((level) => (
          <i
            key={level}
            className={`activity-day activity-level-${level}`}
            aria-hidden="true"
          />
        ))}
        <span>Больше</span>
      </div>
    </section>
  );
}

export function percent(score: number, maxScore: number) {
  return maxScore > 0 ? `${Math.round((score / maxScore) * 100)}%` : "—";
}

function ResultMetric({
  label,
  result,
  color,
  go,
}: {
  label: string;
  result?: ResultSummary | null;
  color: string;
  go?: (id: string) => void;
}) {
  return (
    <article className={`metric-card metric-${color}`}>
      <h2>{label}</h2>
      <strong>{result ? percent(result.score, result.maxScore) : "—"}</strong>
      {result && (
        <>
          <p>
            {result.score} / {result.maxScore} баллов
          </p>
          <button className="text-link" onClick={() => go?.(result.id)}>
            {result.title}
          </button>
        </>
      )}
    </article>
  );
}

export function SummaryMetrics({
  statistics,
  go,
}: {
  statistics: LearningStatistics;
  go?: (id: string) => void;
}) {
  return (
    <section className="metrics-grid portal-card" aria-label="Общие результаты">
      <ResultMetric
        label="Лучший результат"
        result={statistics.best}
        color="gold"
        go={go}
      />
      <ResultMetric
        label="Последний результат"
        result={statistics.latest}
        color="blue"
        go={go}
      />
      <article className="metric-card metric-green">
        <h2>Решено тестов</h2>
        <strong>{statistics.completed}</strong>
      </article>
      <article className="metric-card metric-purple">
        <h2>Решено заданий</h2>
        <strong>{statistics.solvedTasks || 0}</strong>
      </article>
    </section>
  );
}

export function StatisticsPanel({
  statistics,
  error,
  busy,
  onRetry,
  go,
  showAll,
}: {
  statistics?: LearningStatistics;
  error: string;
  busy: boolean;
  onRetry: () => void;
  go?: (id: string) => void;
  showAll?: () => void;
}) {
  return (
    <>
      <PageHeading title="Моя статистика" />
      {error ? (
        <div role="alert" className="portal-error">
          <p>{error}</p>
          <button disabled={busy} onClick={onRetry}>
            Повторить загрузку статистики
          </button>
        </div>
      ) : !statistics ? (
        <p>{busy ? "Загружаем статистику…" : "Статистика пока недоступна."}</p>
      ) : (
        <>
          <SummaryMetrics statistics={statistics} go={go} />
          <section className="portal-card recent-results">
            <h2>Недавние результаты</h2>
            {!statistics.recent?.length && (
              <p className="portal-muted">
                Вы ещё не завершили ни одного теста.
              </p>
            )}
            <div className="attempt-list">
              {statistics.recent?.map((result) => (
                <article className="attempt-row" key={result.id}>
                  <time>
                    {result.completedAt
                      ? new Date(result.completedAt).toLocaleDateString(
                          "ru-RU",
                          { timeZone: "Europe/Moscow" },
                        )
                      : ""}
                  </time>
                  <button className="text-link" onClick={() => go?.(result.id)}>
                    {result.title}
                  </button>
                  <strong>
                    {result.score} / {result.maxScore}
                  </strong>
                  <strong
                    className={
                      result.maxScore && result.score / result.maxScore >= 0.8
                        ? "score-good"
                        : "score-average"
                    }
                  >
                    {percent(result.score, result.maxScore)}
                  </strong>
                </article>
              ))}
            </div>
            {showAll && (
              <button className="portal-primary full-width" onClick={showAll}>
                Показать все результаты →
              </button>
            )}
          </section>
          <ActivityCalendar statistics={statistics} />
          <section className="portal-card">
            <h2>Покрытие тем</h2>
            <div className="coverage-list">
              {statistics.coverage?.map((topic) => (
                <article className="coverage-row" key={topic.tag}>
                  <h3>{topic.tag}</h3>
                  <p className="portal-muted">
                    {topic.solved} / {topic.available} заданий
                  </p>
                  <progress
                    value={topic.solved}
                    max={topic.available || 1}
                    aria-label={`Покрытие темы ${topic.tag}`}
                  />
                </article>
              ))}
              {!statistics.coverage?.length && (
                <p className="portal-muted">
                  В банке пока нет опубликованных заданий.
                </p>
              )}
            </div>
          </section>
          <section className="portal-card">
            <h2>Результаты по темам</h2>
            {!statistics.topics.length ? (
              <p className="portal-muted">
                Завершите первый тест, чтобы увидеть результаты по темам.
              </p>
            ) : (
              <div className="topic-results">
                {statistics.topics.map((topic) => (
                  <article key={topic.tag} className="topic-result">
                    <div>
                      <h3>{topic.tag}</h3>
                      <p className="portal-muted">
                        Заданий пройдено: {topic.tasks}
                      </p>
                    </div>
                    <div className="topic-score">
                      <strong>{percent(topic.score, topic.maxScore)}</strong>
                      <p className="portal-muted">
                        {topic.score} / {topic.maxScore} баллов
                      </p>
                      {topic.maxScore > 0 && (
                        <meter
                          min={0}
                          max={100}
                          value={Math.round(
                            (topic.score / topic.maxScore) * 100,
                          )}
                          aria-label={`Доля набранных баллов по теме ${topic.tag}`}
                        />
                      )}
                    </div>
                  </article>
                ))}
              </div>
            )}
          </section>
        </>
      )}
    </>
  );
}
